import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Checkbox,
  CheckboxGroup,
  Stack,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
  Badge,
  SimpleGrid,
  useToast,
  FormControl,
  FormLabel,
  Switch,
  Select,
} from '@chakra-ui/react';
import CrosstabDisplay from './CrosstabDisplay';
import fetchWithAuth from '../../utils/fetchWithAuth';

const LABEL_MAP = {
  'Age_Group': 'Age Group',
  'age_group': 'Age Group',
  'gender': 'Gender',
  'Party': 'Party',
  'Homeowner': 'Homeowner',
  'vote_propensity_score': 'Vote Propensity',
  'ethnicity': 'Ethnicity',
  'Race': 'Race',
  'Education': 'Education',
  'Income': 'Income',
  'MaritalStatus': 'Marital Status',
  'Congressional_District': 'Congressional District',
  'State_Senate': 'State Senate',
  'State_House': 'State House',
};

function cleanLabel(raw) {
  if (LABEL_MAP[raw]) return LABEL_MAP[raw];
  // Title-case and replace underscores
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * CrosstabBuilder - Interactive crosstab generation interface
 *
 * Allows users to:
 * - Select demographic dimensions
 * - Select question responses as break variables
 * - Choose questions to analyze
 * - Apply weighting
 * - View results with statistics
 */
function CrosstabBuilder({ poll, pollId }) {
  const [demographics, setDemographics] = useState([]);
  const [questionDimensions, setQuestionDimensions] = useState([]);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [useWeights, setUseWeights] = useState(false);
  const [confidenceLevel, setConfidenceLevel] = useState(0.95);
  const [loading, setLoading] = useState(false);
  const [crosstabs, setCrosstabs] = useState(null);
  const [error, setError] = useState(null);
  const [hasWeights, setHasWeights] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchAvailableDimensions();
    checkForWeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchAvailableDimensions = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/available-demographics/`);
      if (!response.ok) {
        throw new Error('Failed to fetch demographics');
      }
      const data = await response.json();
      setDemographics(data.demographics || data.dimensions || []);
      setQuestionDimensions(data.question_dimensions || []);
    } catch (err) {
      console.error('Error fetching dimensions:', err);
      toast({
        title: 'Error',
        description: 'Failed to load demographic dimensions',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const checkForWeights = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/active-weights/`);
      if (response.ok) {
        const data = await response.json();
        setHasWeights(data.has_weights || false);
      }
    } catch (err) {
      console.error('Error checking weights:', err);
    }
  };

  // Question dimension IDs that are selected as break variables
  const selectedQuestionBreakIds = selectedDimensions.filter(d => d.startsWith('q:'));
  // Extract question IDs from selected question breaks to exclude from "analyze" list
  const breakQuestionIds = new Set(
    selectedQuestionBreakIds.map(d => {
      const parts = d.split(':');
      return parseInt(parts[1], 10);
    })
  );
  // Available questions for analysis (exclude those used as break variables)
  const availableQuestions = (poll.questions || []).filter(q => !breakQuestionIds.has(q.id));

  const handleGenerateCrosstabs = async () => {
    if (selectedDimensions.length === 0) {
      toast({
        title: 'No dimensions selected',
        description: 'Please select at least one demographic or question dimension',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (selectedQuestions.length === 0) {
      toast({
        title: 'No questions selected',
        description: 'Please select at least one question',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`/api/polls/${pollId}/crosstab/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question_ids: selectedQuestions,
          dimensions: selectedDimensions,
          weighted: useWeights,
          confidence_level: confidenceLevel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate crosstabs');
      }

      const data = await response.json();
      setCrosstabs(data.crosstabs || []);

      toast({
        title: 'Success!',
        description: `Generated ${data.count} crosstab${data.count !== 1 ? 's' : ''}`,
        status: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('Error generating crosstabs:', err);
      setError(err.message);
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllDemographics = () => {
    const currentDemoSelections = selectedDimensions.filter(d => !d.startsWith('q:'));
    if (currentDemoSelections.length === demographics.length) {
      // Deselect all demographics, keep question selections
      setSelectedDimensions(selectedDimensions.filter(d => d.startsWith('q:')));
    } else {
      // Select all demographics, keep question selections
      const questionSelections = selectedDimensions.filter(d => d.startsWith('q:'));
      setSelectedDimensions([...demographics, ...questionSelections]);
    }
  };

  const handleSelectAllQuestionBreaks = () => {
    const currentQSelections = selectedDimensions.filter(d => d.startsWith('q:'));
    const allQIds = questionDimensions.map(qd => qd.id);
    if (currentQSelections.length === allQIds.length) {
      // Deselect all question breaks, keep demographic selections
      setSelectedDimensions(selectedDimensions.filter(d => !d.startsWith('q:')));
    } else {
      // Select all question breaks, keep demographic selections
      const demoSelections = selectedDimensions.filter(d => !d.startsWith('q:'));
      setSelectedDimensions([...demoSelections, ...allQIds]);
    }
  };

  const handleSelectAllQuestions = () => {
    const questionIds = availableQuestions.map(q => q.id);
    if (selectedQuestions.length === questionIds.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questionIds);
    }
  };

  const handleRunAll = async () => {
    // Select all demographics + all question dimensions + all questions
    const allDims = [...demographics, ...questionDimensions.map(qd => qd.id)];
    const allQuestionIds = (poll.questions || []).map(q => q.id);

    setSelectedDimensions(allDims);
    setSelectedQuestions(allQuestionIds);

    // Run after state is set — use the values directly
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`/api/polls/${pollId}/crosstab/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question_ids: allQuestionIds,
          dimensions: allDims,
          weighted: useWeights,
          confidence_level: confidenceLevel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate crosstabs');
      }

      const data = await response.json();
      setCrosstabs(data.crosstabs || []);

      toast({
        title: 'Success!',
        description: `Generated ${data.count} crosstab${data.count !== 1 ? 's' : ''}`,
        status: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('Error generating crosstabs:', err);
      setError(err.message);
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDimensionToggle = (dim) => {
    setSelectedDimensions(prev =>
      prev.includes(dim) ? prev.filter(d => d !== dim) : [...prev, dim]
    );
  };

  const handleQuestionToggle = (qId) => {
    setSelectedQuestions(prev =>
      prev.includes(qId) ? prev.filter(id => id !== qId) : [...prev, qId]
    );
  };

  // Remove questions from selectedQuestions if they become break variables
  useEffect(() => {
    if (breakQuestionIds.size > 0) {
      setSelectedQuestions(prev => prev.filter(qId => !breakQuestionIds.has(qId)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDimensions]);

  const selectedDemoCount = selectedDimensions.filter(d => !d.startsWith('q:')).length;
  const selectedQBreakCount = selectedDimensions.filter(d => d.startsWith('q:')).length;

  return (
    <VStack spacing={6} align="stretch">
      {/* Instructions */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Generate Crosstabs</Text>
          <Text fontSize="sm" mt={1}>
            Break survey responses by demographics or other question responses.
            Select dimensions on the left and questions to analyze on the right.
          </Text>
        </Box>
      </Alert>

      {/* Run All Button */}
      <Button
        colorScheme="teal"
        variant="outline"
        size="sm"
        onClick={handleRunAll}
        isLoading={loading}
        loadingText="Generating..."
      >
        Run All (all demographics + all questions)
      </Button>

      {/* Configuration Section */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Left Panel: Break By */}
        <Box
          p={5}
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <Heading size="sm" mb={4}>Break By</Heading>

          {/* Demographics */}
          <Box mb={4}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                Demographics
              </Text>
              <Button size="xs" variant="link" onClick={handleSelectAllDemographics}>
                {selectedDemoCount === demographics.length ? 'Deselect All' : 'Select All'}
              </Button>
            </HStack>

            {demographics.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                No demographic data available
              </Text>
            ) : (
              <Stack spacing={2} maxH="200px" overflowY="auto">
                {demographics.map(dim => (
                  <Checkbox
                    key={dim}
                    isChecked={selectedDimensions.includes(dim)}
                    onChange={() => handleDimensionToggle(dim)}
                  >
                    <Text fontSize="sm">{cleanLabel(dim)}</Text>
                  </Checkbox>
                ))}
              </Stack>
            )}
          </Box>

          {/* Question Responses */}
          {questionDimensions.length > 0 && (
            <Box>
              <Divider mb={3} />
              <HStack justify="space-between" mb={2}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                  Question Responses
                </Text>
                <Button size="xs" variant="link" onClick={handleSelectAllQuestionBreaks}>
                  {selectedQBreakCount === questionDimensions.length ? 'Deselect All' : 'Select All'}
                </Button>
              </HStack>

              <Stack spacing={2} maxH="200px" overflowY="auto">
                {questionDimensions.map(qd => (
                  <Checkbox
                    key={qd.id}
                    isChecked={selectedDimensions.includes(qd.id)}
                    onChange={() => handleDimensionToggle(qd.id)}
                  >
                    <Text fontSize="sm" noOfLines={2}>{qd.label}</Text>
                  </Checkbox>
                ))}
              </Stack>
            </Box>
          )}

          <Text fontSize="xs" color="gray.500" mt={3}>
            {selectedDemoCount} demographic{selectedDemoCount !== 1 ? 's' : ''}
            {selectedQBreakCount > 0 && `, ${selectedQBreakCount} question${selectedQBreakCount !== 1 ? 's' : ''}`}
            {' '}selected
          </Text>
        </Box>

        {/* Questions to Analyze */}
        <Box
          p={5}
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <HStack justify="space-between" mb={4}>
            <Heading size="sm">Questions to Analyze</Heading>
            <Button size="xs" variant="link" onClick={handleSelectAllQuestions}>
              {selectedQuestions.length === availableQuestions.length ? 'Deselect All' : 'Select All'}
            </Button>
          </HStack>

          {availableQuestions.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              {breakQuestionIds.size > 0
                ? 'All questions are being used as break variables'
                : 'No questions in this poll'}
            </Text>
          ) : (
            <Stack spacing={2} maxH="300px" overflowY="auto">
              {availableQuestions.map(question => (
                <Checkbox
                  key={question.id}
                  isChecked={selectedQuestions.includes(question.id)}
                  onChange={() => handleQuestionToggle(question.id)}
                >
                  <Text fontSize="sm" noOfLines={2}>
                    Q{question.order || question.id}: {question.text}
                  </Text>
                </Checkbox>
              ))}
            </Stack>
          )}

          <Text fontSize="xs" color="gray.500" mt={3}>
            {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} selected
          </Text>
        </Box>
      </SimpleGrid>

      {/* Options */}
      <Box
        p={5}
        bg="gray.50"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={4}>Options</Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="use-weights" mb={0} fontSize="sm">
              Apply Demographic Weights
              {!hasWeights && (
                <Badge ml={2} colorScheme="orange" fontSize="xs">
                  No weights configured
                </Badge>
              )}
            </FormLabel>
            <Switch
              id="use-weights"
              isChecked={useWeights}
              onChange={(e) => setUseWeights(e.target.checked)}
              isDisabled={!hasWeights}
              colorScheme="blue"
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Confidence Level</FormLabel>
            <Select
              value={confidenceLevel}
              onChange={(e) => setConfidenceLevel(parseFloat(e.target.value))}
              size="sm"
            >
              <option value={0.90}>90% (z = 1.645)</option>
              <option value={0.95}>95% (z = 1.960)</option>
              <option value={0.99}>99% (z = 2.576)</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        {useWeights && !hasWeights && (
          <Alert status="warning" mt={3} fontSize="sm">
            <AlertIcon />
            No active weighting scheme. Visit the Weighting tab to configure weights.
          </Alert>
        )}
      </Box>

      {/* Generate Button */}
      <Button
        colorScheme="blue"
        size="lg"
        onClick={handleGenerateCrosstabs}
        isLoading={loading}
        loadingText="Generating Crosstabs..."
        isDisabled={selectedDimensions.length === 0 || selectedQuestions.length === 0}
      >
        Generate Crosstabs
      </Button>

      {/* Error Display */}
      {error && (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Results */}
      {loading && (
        <VStack py={8} spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text>Calculating crosstabs with statistical tests...</Text>
        </VStack>
      )}

      {crosstabs && crosstabs.length > 0 && (
        <>
          <Divider />
          <Heading size="md">Results</Heading>
          <VStack spacing={6} align="stretch">
            {crosstabs.map((crosstab, index) => (
              <CrosstabDisplay
                key={index}
                crosstab={crosstab}
                index={index}
              />
            ))}
          </VStack>
        </>
      )}

      {crosstabs && crosstabs.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          No crosstab data generated. This may happen if there are no responses yet.
        </Alert>
      )}
    </VStack>
  );
}

export default CrosstabBuilder;
